import { useEffect } from "react";
import { findAccountType } from "./utils";

const accountNumbers = {
  Assets: 1000,
  Liabilities: 2000,
  Equity: 3000,
  Income: 4000,
  Expenses: 5000,
};

// Compare two dollar amounts in integer cents so float representation error
// (e.g. 0.1 + 0.2 !== 0.3) can't block a legitimately balanced allocation.
export const toCents = (value) => Math.round(parseFloat(value || 0) * 100);

const checkIfAccountNumberTaken = ({
  accountNumber,
  existingAccountNumbers,
}) => {
  if (accountNumber !== undefined) {
    const accountNumberEntry = accountNumber.toString();
    // Normalize the stored side too — a legacy int-typed accountNumber must
    // still match the string form entry. Guard against a missing list so the
    // synchronous validateForm composition (which omits existingAccountNumbers)
    // never throws on undefined.find.
    const alreadyTaken = (existingAccountNumbers || []).find(
      (stored) => String(stored) === accountNumberEntry,
    );
    return alreadyTaken;
  } else return;
};

export const useAccountNumberValidation = ({
  number,
  setAccountEntryErrors,
  setAccountEntryFeedback,
  existingAccountNumbers,
  accounts,
  editingAccountId,
  originalAccountNumber,
}) =>
  useEffect(() => {
    if (number !== "") {
      const alreadyTaken = checkIfAccountNumberTaken({
        accountNumber: number,
        existingAccountNumbers,
      });

      // If editing and the number hasn't changed, don't show error. Normalize
      // BOTH sides — otherwise editing a legacy int-numbered account (stored as
      // 5000, form value "5000") would falsely read as "taken".
      const isEditingWithSameNumber =
        editingAccountId && String(originalAccountNumber) === String(number);

      if (alreadyTaken && !isEditingWithSameNumber) {
        setAccountEntryErrors((prev) => ({
          ...prev,
          accountNumber: true,
        }));
        setAccountEntryFeedback((prev) => ({
          ...prev,
          accountNumber: "This number is already taken",
        }));
      } else {
        setAccountEntryErrors((prev) => ({
          ...prev,
          accountNumber: false,
        }));
        setAccountEntryFeedback((prev) => ({
          ...prev,
          accountNumber: "",
        }));
      }
    }
  }, [
    number,
    existingAccountNumbers,
    accounts,
    editingAccountId,
    originalAccountNumber,
  ]);

// Each validator runs synchronously, pushes its result through the functional
// setters (so concurrent validators never clobber each other's flags), and
// RETURNS its verdict (true = valid) so validateForm can compose them directly
// instead of reading a lagging errors ref after awaiting no-op promises.
export const validateAccountType = ({
  accountEntryData,
  setAccountEntryErrors,
  setAccountEntryFeedback,
}) => {
  if (accountEntryData.type === "") {
    setAccountEntryErrors((prev) => ({ ...prev, type: true }));
    setAccountEntryFeedback((prev) => ({
      ...prev,
      type: "Please select an account type",
    }));
    return false;
  } else if (
    accountEntryData.accountNumber !== "" &&
    findAccountType(accountEntryData.accountNumber) !== accountEntryData.type
  ) {
    setAccountEntryErrors((prev) => ({ ...prev, type: true }));
    setAccountEntryFeedback((prev) => ({
      ...prev,
      type: `For ${
        accountEntryData.type
      } accounts, the account number must be in the ${
        accountNumbers[accountEntryData.type]
      }'s`,
    }));
    return false;
  }
  setAccountEntryErrors((prev) => ({ ...prev, type: false }));
  setAccountEntryFeedback((prev) => ({ ...prev, type: "" }));
  return true;
};

// A trailing dot ("5000.") must never be accepted — it's stored verbatim as a
// string, so "5000" and "5000." would coexist as distinct account numbers.
export const hasTrailingDot = (value) => /\.$/.test(String(value ?? ""));

// GH #445: a 3xxx number is typed Equity by findAccountType and lands in the
// "Fund-Linked Accounts" group with no fund doc behind it — an orphan that
// used to crash the journal-entry form (#449) and that only fund creation is
// allowed to produce. First-digit test, so 3000-3999 and 3100.01 alike.
// .trim() because findReservedEquityError applies this to un-validated import
// values, where a leading space would otherwise make parseInt return NaN and
// wave a 3xxx row straight through.
export const isReservedEquityNumber = (value) =>
  parseInt(String(value ?? "").trim().slice(0, 1)) === 3;

export const RESERVED_EQUITY_NUMBER_MESSAGE =
  "Account numbers starting with 3 are reserved for fund equity accounts. " +
  "Create a fund instead — it creates its equity account for you.";

export const validateAccountNumber = ({
  accountEntryData,
  setAccountEntryErrors,
  setAccountEntryFeedback,
  accountDataToEdit,
  existingAccountNumbers,
}) => {
  if (accountEntryData.accountNumber === "") {
    setAccountEntryErrors((prev) => ({ ...prev, accountNumber: true }));
    setAccountEntryFeedback((prev) => ({
      ...prev,
      accountNumber: "Please enter an account number",
    }));
    return false;
  } else if (hasTrailingDot(accountEntryData.accountNumber)) {
    setAccountEntryErrors((prev) => ({ ...prev, accountNumber: true }));
    setAccountEntryFeedback((prev) => ({
      ...prev,
      accountNumber: "Account number can't end with a decimal point",
    }));
    return false;
  } else if (isReservedEquityNumber(accountEntryData.accountNumber)) {
    setAccountEntryErrors((prev) => ({ ...prev, accountNumber: true }));
    setAccountEntryFeedback((prev) => ({
      ...prev,
      accountNumber: RESERVED_EQUITY_NUMBER_MESSAGE,
    }));
    return false;
  } else if (
    checkIfAccountNumberTaken({
      accountNumber: accountEntryData.accountNumber,
      existingAccountNumbers,
    }) &&
    //If the account number is taken, but it's the same as the account being edited, don't throw an error
    //(normalize both sides so an int-typed stored number still exempts itself)
    (!accountDataToEdit?.accountDatabaseId ||
      String(accountDataToEdit.accountNumber) !==
        String(accountEntryData.accountNumber))
  ) {
    //AccountNumber already exists
    setAccountEntryErrors((prev) => ({ ...prev, accountNumber: true }));
    setAccountEntryFeedback((prev) => ({
      ...prev,
      accountNumber: "Account number already exists",
    }));
    return false;
  } else if (
    accountEntryData.type !== "" &&
    findAccountType(accountEntryData.accountNumber) !== accountEntryData.type
  ) {
    setAccountEntryErrors((prev) => ({ ...prev, accountNumber: true }));
    setAccountEntryFeedback((prev) => ({
      ...prev,
      accountNumber: `For ${
        accountEntryData.type
      } accounts, the account number must start with ${
        accountNumbers[accountEntryData.type]
      }`,
    }));
    return false;
  }
  setAccountEntryErrors((prev) => ({ ...prev, accountNumber: false }));
  setAccountEntryFeedback((prev) => ({ ...prev, accountNumber: "" }));
  return true;
};

export const validateAccountName = ({
  accountEntryData,
  setAccountEntryErrors,
  setAccountEntryFeedback,
}) => {
  if (accountEntryData.accountName === "") {
    setAccountEntryErrors((prev) => ({ ...prev, accountName: true }));
    setAccountEntryFeedback((prev) => ({
      ...prev,
      accountName: "Please enter an account name",
    }));
    return false;
  }
  setAccountEntryErrors((prev) => ({ ...prev, accountName: false }));
  setAccountEntryFeedback((prev) => ({ ...prev, accountName: "" }));
  return true;
};

export const validateGroup = ({
  accountEntryData,
  setAccountEntryErrors,
  setAccountEntryFeedback,
}) => {
  if (accountEntryData.group === "") {
    setAccountEntryErrors((prevState) => ({
      ...prevState,
      group: true,
    }));
    setAccountEntryFeedback((prevState) => ({
      ...prevState,
      group: "Please enter or select a group",
    }));
    return false;
  }
  setAccountEntryErrors((prevState) => ({
    ...prevState,
    group: false,
  }));
  setAccountEntryFeedback((prevState) => ({
    ...prevState,
    group: "",
  }));
  return true;
};

//Validate sub account, which is required if the account is a sub account
export const validateSubAccount = ({
  accountEntryData,
  setAccountEntryErrors,
  setAccountEntryFeedback,
}) => {
  if (accountEntryData.subAccount && !accountEntryData.subAccountOf) {
    setAccountEntryErrors((prev) => ({ ...prev, subAccount: true }));
    setAccountEntryFeedback((prev) => ({
      ...prev,
      subAccount: "Please select a parent account or turn off sub account",
    }));
    return false;
  }
  setAccountEntryErrors((prev) => ({ ...prev, subAccount: false }));
  setAccountEntryFeedback((prev) => ({ ...prev, subAccount: "" }));
  return true;
};

//Validate the starting balance of each fund summed is equal to the startingBalance, but only if startingBalance is > 0
export const validateStartingBalance = ({
  accountEntryData,
  setRemainingStartingToAssign,
  setAccountEntryErrors,
  setAccountEntryFeedback,
}) => {
  if (accountEntryData.startingBalance > 0) {
    const fundStartingBalances = Object.values(
      accountEntryData.fundStartingBalances,
    );

    const sumOfFundStartingBalances = fundStartingBalances.reduce(
      (acc, curr) => {
        if (curr !== "") {
          return acc + parseFloat(curr || 0);
        } else {
          return acc;
        }
      },
      0,
    );

    // Compare in integer cents (float !== blocked balanced allocations like
    // 0.1 + 0.2). setRemainingStartingToAssign still shows the raw remainder.
    const balanced =
      toCents(sumOfFundStartingBalances) ===
      toCents(accountEntryData.startingBalance);

    if (!balanced && accountEntryData.startingBalanceDate === undefined) {
      setRemainingStartingToAssign(
        accountEntryData.startingBalance - sumOfFundStartingBalances,
      );
      setAccountEntryErrors((prev) => ({
        ...prev,
        startingBalance: true,
        startingBalanceDate: true,
      }));
      setAccountEntryFeedback((prev) => ({
        ...prev,
        startingBalance: "Fund starting balances must equal starting balance",
        startingBalanceDate: "Please select a starting balance date",
      }));
      return false;
    } else if (!balanced) {
      setRemainingStartingToAssign(
        accountEntryData.startingBalance - sumOfFundStartingBalances,
      );
      setAccountEntryErrors((prev) => ({
        ...prev,
        startingBalance: true,
        startingBalanceDate: false,
      }));
      setAccountEntryFeedback((prev) => ({
        ...prev,
        startingBalance: "Fund starting balances must equal starting balance",
        startingBalanceDate: "",
      }));
      return false;
    } else {
      setRemainingStartingToAssign(0);
      setAccountEntryErrors((prev) => ({
        ...prev,
        startingBalance: false,
        startingBalanceDate: false,
      }));
      setAccountEntryFeedback((prev) => ({
        ...prev,
        startingBalance: "",
        startingBalanceDate: "",
      }));
      return true;
    }
  } else {
    setRemainingStartingToAssign(0);
    setAccountEntryErrors((prev) => ({
      ...prev,
      startingBalance: false,
      startingBalanceDate: false,
    }));
    setAccountEntryFeedback((prev) => ({
      ...prev,
      startingBalance: "",
      startingBalanceDate: "",
    }));
    return true;
  }
};

export const validateFundName = ({
  fundEntryData,
  setFundEntryErrors,
  setFundEntryFeedback,
}) => {
  if (fundEntryData.fundName === "") {
    setFundEntryErrors((prev) => ({ ...prev, fundName: true }));
    setFundEntryFeedback((prev) => ({
      ...prev,
      fundName: "Please enter an fund name",
    }));
    return false;
  }
  setFundEntryErrors((prev) => ({ ...prev, fundName: false }));
  setFundEntryFeedback((prev) => ({ ...prev, fundName: "" }));
  return true;
};

const checkIfFundNumberTaken = ({ fundNumber, existingFundNumbers }) => {
  if (fundNumber !== undefined) {
    const fundNumberEntry = fundNumber.toString();
    // Normalize the stored side too (mirror of checkIfAccountNumberTaken).
    const alreadyTaken = (existingFundNumbers || []).find(
      (stored) => String(stored) === fundNumberEntry,
    );
    return alreadyTaken;
  } else return;
};

export const useFundNumberValidation = ({
  number,
  setFundEntryErrors,
  setFundEntryFeedback,
  existingFundNumbers,
  funds,
  fundDataToEdit,
  fundEntryData,
}) =>
  useEffect(() => {
    if (number !== "") {
      const alreadyTaken =
        checkIfFundNumberTaken({
          fundNumber: number,
          existingFundNumbers,
        }) &&
        (!fundDataToEdit?.fundDatabaseId ||
          String(fundDataToEdit.fundNumber) !==
            String(fundEntryData.fundNumber));
      if (alreadyTaken) {
        setFundEntryErrors((prev) => ({ ...prev, fundNumber: true }));
        setFundEntryFeedback((prev) => ({
          ...prev,
          fundNumber: "This number is already taken",
        }));
      } else {
        setFundEntryErrors((prev) => ({ ...prev, fundNumber: false }));
        setFundEntryFeedback((prev) => ({ ...prev, fundNumber: "" }));
      }
    }
  }, [number, existingFundNumbers, funds]);

export const validateFundNumber = ({
  fundEntryData,
  setFundEntryErrors,
  setFundEntryFeedback,
  fundDataToEdit,
}) => {
  if (fundEntryData.fundNumber === "") {
    setFundEntryErrors((prev) => ({ ...prev, fundNumber: true }));
    setFundEntryFeedback((prev) => ({
      ...prev,
      fundNumber: "Please enter an fund number",
    }));
    return false;
  } else if (hasTrailingDot(fundEntryData.fundNumber)) {
    setFundEntryErrors((prev) => ({ ...prev, fundNumber: true }));
    setFundEntryFeedback((prev) => ({
      ...prev,
      fundNumber: "Fund number can't end with a decimal point",
    }));
    return false;
  } else if (
    parseInt(fundEntryData.fundNumber.toString().slice(0, 1)) !== 3
  ) {
    setFundEntryErrors((prev) => ({ ...prev, fundNumber: true }));
    setFundEntryFeedback((prev) => ({
      ...prev,
      fundNumber: `Fund Numbers must start with 3, since they have tied equity accounts`,
    }));
    return false;
  } else if (
    checkIfFundNumberTaken(fundEntryData.fundNumber) &&
    //If the fund number is taken, but it's the same as the fund being edited, don't throw an error
    (!fundDataToEdit.fundDatabaseId ||
      String(fundDataToEdit.fundNumber) !== String(fundEntryData.fundNumber))
  ) {
    //FundNumber already exists
    setFundEntryErrors((prev) => ({ ...prev, fundNumber: true }));
    setFundEntryFeedback((prev) => ({
      ...prev,
      fundNumber: "Fund number already exists",
    }));
    return false;
  }
  setFundEntryErrors((prev) => ({ ...prev, fundNumber: false }));
  setFundEntryFeedback((prev) => ({ ...prev, fundNumber: "" }));
  return true;
};
